import {
  UndoRedoAction,
  UndoRedoActionType,
  CellEditAction,
  RowAddAction,
  RowDeleteAction,
  BulkFillAction,
  UndoRedoState,
  UndoRedoConfig,
  UndoRedoCallbacks,
  DeletedRowData,
} from "./undo-redo.types";
import { CellUpdatePayload } from "@modules/spreadsheets/dto/spreadsheet/type";
import { SpreadsheetTableColumnId } from "../table.types";

/**
 * Undo/Redo Manager Class
 * Handles the complete undo/redo functionality for spreadsheet operations
 */
export class UndoRedoManager {
  private state: UndoRedoState;
  private config: Required<UndoRedoConfig>;
  private callbacks: UndoRedoCallbacks;

  constructor(config: UndoRedoConfig = {}, callbacks: UndoRedoCallbacks) {
    this.config = {
      maxStackSize: config.maxStackSize || 50,
      debounceMs: config.debounceMs || 500,
    };

    this.callbacks = callbacks;

    this.state = {
      undoStack: [],
      redoStack: [],
      maxStackSize: this.config.maxStackSize,
      isPerformingAction: false,
    };
  }

  /**
   * Update the callbacks without reinitializing the manager
   */
  updateCallbacks(callbacks: UndoRedoCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Get current state (for React integration)
   */
  getState(): UndoRedoState {
    return { ...this.state };
  }

  /**
   * Check if undo is available
   */
  canUndo(): boolean {
    return this.state.undoStack.length > 0 && !this.state.isPerformingAction;
  }

  /**
   * Check if redo is available
   */
  canRedo(): boolean {
    return this.state.redoStack.length > 0 && !this.state.isPerformingAction;
  }

  /**
   * Track a cell edit action
   */
  trackCellEdit(
    currentPayload: CellUpdatePayload,
    rowIndex: number,
    columnId: SpreadsheetTableColumnId,
    previousPayload: CellUpdatePayload,
    popPreviousUndo: boolean = false
  ): void {
    if (this.state.isPerformingAction) {
      return;
    }

    // If popPreviousUndo is true, remove the most recent undo action
    if (popPreviousUndo) {
      this.popPreviousUndoAction();
    }

    const action: CellEditAction = {
      id: this.generateActionId(),
      type: UndoRedoActionType.CELL_EDIT,
      timestamp: Date.now(),
      description: `Edit cell (Row ${rowIndex + 1}, Column ${columnId}) - ${
        previousPayload.value
      } → ${currentPayload.value}`,
      currentPayload,
      previousPayload,
      rowIndex,
      columnId,
    };

    this.addToUndoStack(action);
    this.clearRedoStack(); // Clear redo stack when new action is performed
  }

  /**
   * Track a row add action
   */
  trackRowAdd(
    rowIndex: number,
    columnId: SpreadsheetTableColumnId,
    globalId: number,
    tableId: number,
    rrn: number
  ): void {
    if (this.state.isPerformingAction) {
      return;
    }

    const action: RowAddAction = {
      id: this.generateActionId(),
      type: UndoRedoActionType.ROW_ADD,
      timestamp: Date.now(),
      description: `Add row at position ${rowIndex + 1}`,
      rowIndex,
      columnId,
      globalId,
      tableId,
      rrn,
    };

    this.addToUndoStack(action);
    this.clearRedoStack(); // Clear redo stack when new action is performed
  }

  /**
   * Track a row delete action (supports both single and bulk operations)
   */
  trackRowDelete(
    deletedRows: Array<{
      rowIndex: number;
      columnId: SpreadsheetTableColumnId;
      globalId: number;
      tableId: number;
      rrn: number;
    deletedRowData: Array<DeletedRowData>;
    }>
  ): void {
    if (this.state.isPerformingAction) {
      return;
    }

    const action: RowDeleteAction = {
      id: this.generateActionId(),
      type: UndoRedoActionType.ROW_DELETE,
      timestamp: Date.now(),
      description: (deletedRows.length === 1 && deletedRows[0])
        ? `Delete row at position ${deletedRows[0].rowIndex + 1}`
        : `Delete ${deletedRows.length} rows`,
      deletedRows,
    };

    this.addToUndoStack(action);
    this.clearRedoStack(); // Clear redo stack when new action is performed
  }

  /**
   * Track a bulk fill action (fill up/down)
   */
  trackBulkFill(
    fillOperations: Array<{
      rowIndex: number;
      columnId: SpreadsheetTableColumnId;
      newValue: string | number;
      originalValue: string | number;
      payload: CellUpdatePayload;
      columnIndex: number
    }>,
    fillDirection: 'up' | 'down'
  ): void {
    if (this.state.isPerformingAction) {
      return; // Don't track during undo/redo operations
    }

    const action: BulkFillAction = {
      id: this.generateActionId(),
      type: UndoRedoActionType.BULK_FILL,
      timestamp: Date.now(),
      description: `Fill ${fillDirection} ${fillOperations.length} cells`,
      fillOperations,
      fillDirection,
    };

    this.addToUndoStack(action);
    this.clearRedoStack(); // Clear redo stack when new action is performed
  }

  /**
   * Undo the last action
   */
  async undo(): Promise<void> {
    if (!this.canUndo()) {
      return;
    }

    const action = this.state.undoStack.pop()!;
    this.state.isPerformingAction = true;

    try {
      // Execute the undo action and get updated action with new RRNs
      const updatedAction = await this.executeUndoAction(action);
      
      // Push the updated action to redo stack (with new RRNs)
      this.state.redoStack.push(updatedAction || action);
    } catch (error) {
      // If undo fails, put the action back
      this.state.undoStack.push(action);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to undo action";
      this.callbacks.onError?.(errorMessage);
    } finally {
      this.state.isPerformingAction = false;
    }
  }

  /**
   * Redo the last undone action
   */
  async redo(): Promise<void> {
    if (!this.canRedo()) {
      return;
    }

    const action = this.state.redoStack.pop()!;
    this.state.isPerformingAction = true;

    try {
      await this.executeRedoAction(action);
      this.state.undoStack.push(action);
    } catch (error) {
      // If redo fails, put the action back
      this.state.redoStack.push(action);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to redo action";
      this.callbacks.onError?.(errorMessage);
    } finally {
      this.state.isPerformingAction = false;
    }
  }

  /**
   * Clear all undo/redo history
   */
  clearHistory(): void {
    this.state.undoStack = [];
    this.state.redoStack = [];
  }

  /**
   * Execute an undo action and return updated action with new RRNs
   */
  private async executeUndoAction(action: UndoRedoAction): Promise<UndoRedoAction | undefined> {
    switch (action.type) {
      case UndoRedoActionType.CELL_EDIT:
        await this.undoCellEdit(action as CellEditAction);
        return undefined; // No RRN updates needed for cell edits
      case UndoRedoActionType.ROW_ADD:
        await this.undoRowAdd(action as RowAddAction);
        return undefined; // No RRN updates needed for row adds
      case UndoRedoActionType.ROW_DELETE:
        return await this.undoRowDelete(action as RowDeleteAction); // Return updated action with new RRNs
      case UndoRedoActionType.BULK_FILL:
        await this.undoBulkFill(action as BulkFillAction);
        return undefined; // No RRN updates needed for bulk fills
      default:
        throw new Error(`Unknown action type: ${(action as any).type}`);
    }
  }

  /**
   * Execute a redo action
   */
  private async executeRedoAction(action: UndoRedoAction): Promise<void> {
    switch (action.type) {
      case UndoRedoActionType.CELL_EDIT:
        await this.redoCellEdit(action as CellEditAction);
        break;
      case UndoRedoActionType.ROW_ADD:
        await this.redoRowAdd(action as RowAddAction);
        break;
      case UndoRedoActionType.ROW_DELETE:
        await this.redoRowDelete(action as RowDeleteAction);
        break;
      case UndoRedoActionType.BULK_FILL:
        await this.redoBulkFill(action as BulkFillAction);
        break;
      default:
        throw new Error(`Unknown action type: ${(action as any).type}`);
    }
  }

  /**
   * Undo a cell edit
   */
  private async undoCellEdit(action: CellEditAction): Promise<void> {
    await this.callbacks.onCellEdit(
      action.previousPayload,
      action.rowIndex,
      action.columnId,
      action.previousPayload.value
    );
  }

  /**
   * Redo a cell edit
   */
  private async redoCellEdit(action: CellEditAction): Promise<void> {
    await this.callbacks.onCellEdit(
      action.currentPayload,
      action.rowIndex,
      action.columnId,
      action.currentPayload.value
    );
  }

  /**
   * Undo a row add (delete the row)
   */
  private async undoRowAdd(action: RowAddAction): Promise<void> {
    await this.callbacks.onRowDelete(
      action.rowIndex,
      action.columnId,
      action.globalId,
      action.tableId,
      action.rrn
    );
  }

  /**
   * Redo a row add (add the row back)
   */
  private async redoRowAdd(action: RowAddAction): Promise<void> {
    await this.callbacks.onRowAdd(
      action.rowIndex,
      action.columnId,
      action.globalId,
      action.tableId,
      action.rrn
    );
  }

  /**
   * Undo a row delete (add the rows back with their data) and return updated action with new RRNs
   */
  private async undoRowDelete(action: RowDeleteAction): Promise<RowDeleteAction> {
    // Sort deleted rows by original row index in descending order to restore from bottom to top
    // This prevents row index shifting issues when inserting multiple rows
    const sortedRows = [...action.deletedRows].sort((a, b) => b.rowIndex - a.rowIndex);
    
    // Check if this is a bulk operation (more than 1 row)
    const isBulkOperation = action.deletedRows.length > 1;
    
    if (isBulkOperation) {
      // BULK OPERATION: Use new batched approach
      return await this.undoRowDeleteBulk(action, sortedRows);
    } else {
      // SINGLE OPERATION: Use existing immediate state update approach
      return await this.undoRowDeleteSingle(action, sortedRows);
    }
  }

  /**
   * Undo single row delete (using working objects approach) and return updated action with new RRNs
   */
  private async undoRowDeleteSingle(action: RowDeleteAction, sortedRows: any[]): Promise<RowDeleteAction> {
    const deletedRow = sortedRows[0]; // Only one row for single operations
    
    // Check if required callbacks exist
    if (!this.callbacks.onRowAddWithoutStateUpdate) {
      console.error("onRowAddWithoutStateUpdate callback not available");
      throw new Error("onRowAddWithoutStateUpdate callback not available");
    }
    if (!this.callbacks.onBulkStateUpdate) {
      console.error("onBulkStateUpdate callback not available");
      throw new Error("onBulkStateUpdate callback not available");
    }
    
    // Step 1: Make API call for row creation (no state updates)
    const apiResponse = await this.callbacks.onRowAddWithoutStateUpdate(
      deletedRow.rowIndex,
      deletedRow.columnId,
      deletedRow.globalId,
      deletedRow.tableId,
      deletedRow.rrn
    );
    
    // Step 2: Prepare cell updates with correct row index
    const cellUpdates: Array<{
      rowIndex: number;
      columnId: SpreadsheetTableColumnId;
      value: string | number;
      payload: CellUpdatePayload;
      columnIndex: number
    }> = [];
    
    if (deletedRow.deletedRowData && deletedRow.deletedRowData.length > 0) {
      const rowCellUpdates = deletedRow.deletedRowData.map((cellData: DeletedRowData) => {
        const fieldInfo = this.callbacks.getFieldInfo?.(cellData.fieldId);
        const systemField = fieldInfo?.system || false;

        const payload: CellUpdatePayload = {
          globalId: deletedRow.globalId,
          fieldId: cellData.fieldId,
          context: 0,
          systemField: systemField,
          ds: false,
          value: cellData.value,
          rrn: apiResponse.rrn,
          tableId: cellData.tableId,
        };

        return {
          rowIndex: deletedRow.rowIndex, // This will be corrected in onBulkStateUpdate
          columnId: cellData.fieldId as SpreadsheetTableColumnId,
          value: cellData.value,
          payload: payload,
          columnIndex: cellData.columnIndex
        };
      });
      cellUpdates.push(...rowCellUpdates);
    }
    
    // Step 3: Make API calls for cell updates (no state updates)
    if (cellUpdates.length > 0) {
      if (!this.callbacks.onBulkCellUpdateWithoutStateUpdate) {
        console.error("onBulkCellUpdateWithoutStateUpdate callback not available");
        throw new Error("onBulkCellUpdateWithoutStateUpdate callback not available");
      }
      await this.callbacks.onBulkCellUpdateWithoutStateUpdate(cellUpdates);
    }
    
    // Step 4: Single batched state update
    await this.callbacks.onBulkStateUpdate({
      rowAdditions: [{
        rowIndex: deletedRow.rowIndex,
        columnId: deletedRow.columnId,
        globalId: deletedRow.globalId,
        tableId: deletedRow.tableId,
        rrn: apiResponse.rrn,
        response: apiResponse.response
      }],
      cellUpdates: cellUpdates,
      totalCountChange: 1
    });
    
    // Return updated action with new RRN for redo stack
    return {
      ...action,
      deletedRows: [{
        ...deletedRow,
        rrn: apiResponse.rrn // Update with new RRN
      }]
    };
  }

  /**
   * Undo bulk row delete (using working objects approach) and return updated action with new RRNs
   */
  private async undoRowDeleteBulk(action: RowDeleteAction, sortedRows: any[]): Promise<RowDeleteAction> {    
    // Check if required callbacks exist
    if (!this.callbacks.onRowAddWithoutStateUpdate) {
      console.error("onRowAddWithoutStateUpdate callback not available");
      throw new Error("onRowAddWithoutStateUpdate callback not available");
    }
    if (!this.callbacks.onBulkStateUpdate) {
      console.error("onBulkStateUpdate callback not available");
      throw new Error("onBulkStateUpdate callback not available");
    }
    
    // Step 1: Make all API calls for row creation (no state updates)
    const bulkRowAddData: Array<{
      rowIndex: number;
      columnId: SpreadsheetTableColumnId;
      globalId: number;
      tableId: number;
      rrn: number;
      response: any;
    }> = [];
    
    for (const deletedRow of sortedRows) {
      const result = await this.callbacks.onRowAddWithoutStateUpdate(
        deletedRow.rowIndex,
        deletedRow.columnId,
        deletedRow.globalId,
        deletedRow.tableId,
        deletedRow.rrn
      );
      
      bulkRowAddData.push({
        rowIndex: deletedRow.rowIndex,
        columnId: deletedRow.columnId,
        globalId: deletedRow.globalId,
        tableId: deletedRow.tableId,
        rrn: result.rrn,
        response: result.response
      });
    }

    // Step 2: Prepare all cell updates
    const allBulkUpdates: Array<{
      rowIndex: number;
      columnId: SpreadsheetTableColumnId;
      value: string | number;
      payload: CellUpdatePayload;
      columnIndex: number
    }> = [];

    // Process rows in original order for cell updates
    const rowsForCellUpdates = [...action.deletedRows].sort((a, b) => a.rowIndex - b.rowIndex);
    
    for (const deletedRow of rowsForCellUpdates) {
      const addResponse = bulkRowAddData.find(data => data.rowIndex === deletedRow.rowIndex);
      
      if (addResponse && deletedRow.deletedRowData && deletedRow.deletedRowData.length > 0) {
        const rowBulkUpdates = deletedRow.deletedRowData.map((cellData) => {
          const fieldInfo = this.callbacks.getFieldInfo?.(cellData.fieldId);
          const systemField = fieldInfo?.system || false;

          const payload: CellUpdatePayload = {
            globalId: deletedRow.globalId,
            fieldId: cellData.fieldId,
            context: 0,
            systemField: systemField,
            ds: false,
            value: cellData.value,
            rrn: addResponse.rrn,
            tableId: cellData.tableId,
          };

          return {
            rowIndex: deletedRow.rowIndex, // This will be corrected in onBulkStateUpdate
            columnId: cellData.fieldId as SpreadsheetTableColumnId,
            value: cellData.value,
            payload: payload,
            columnIndex: cellData.columnIndex
          };
        });

        allBulkUpdates.push(...rowBulkUpdates);
      }
    }

    // Step 3: Make all cell update API calls (no state updates)
    if (allBulkUpdates.length > 0) {
      if (!this.callbacks.onBulkCellUpdateWithoutStateUpdate) {
        console.error("onBulkCellUpdateWithoutStateUpdate callback not available");
        throw new Error("onBulkCellUpdateWithoutStateUpdate callback not available");
      }
      await this.callbacks.onBulkCellUpdateWithoutStateUpdate(allBulkUpdates);
    }

    // Step 4: Single batched state update at the end
    await this.callbacks.onBulkStateUpdate({
      rowAdditions: bulkRowAddData,
      cellUpdates: allBulkUpdates,
      totalCountChange: action.deletedRows.length
    });
    
    // Return updated action with new RRNs for redo stack
    return {
      ...action,
      deletedRows: action.deletedRows.map(originalRow => {
        const updatedRow = bulkRowAddData.find(data => data.rowIndex === originalRow.rowIndex);
        return {
          ...originalRow,
          rrn: updatedRow?.rrn || originalRow.rrn // Update with new RRN
        };
      })
    };
  }

  /**
   * Redo a row delete (delete the rows again with updated RRNs using batched approach)
   */
  private async redoRowDelete(action: RowDeleteAction): Promise<void> {
    // Check if required callbacks exist
    if (!this.callbacks.onRowDeleteWithoutStateUpdate) {
      console.error("onRowDeleteWithoutStateUpdate callback not available");
      throw new Error("onRowDeleteWithoutStateUpdate callback not available");
    }
    if (!this.callbacks.onBulkStateUpdateForRedo) {
      console.error("onBulkStateUpdateForRedo callback not available");
      throw new Error("onBulkStateUpdateForRedo callback not available");
    }
    
    // Get current row data to find the correct row indices
    const currentRowData = this.callbacks.getCurrentRowData?.();
    if (!currentRowData) {
      console.error("Cannot get current row data for redo operation");
      throw new Error("Cannot get current row data for redo operation");
    }
    
    // Find the current row indices for each row that needs to be deleted
    const rowsToDelete = action.deletedRows.map(actionRow => {
      // Find the current row by matching the updated RRN
      const currentRow = currentRowData.find(row => row.rrn === actionRow.rrn);
      if (!currentRow) {
        console.error(`Cannot find current row with RRN ${actionRow.rrn} for redo`);
        throw new Error(`Cannot find current row with RRN ${actionRow.rrn} for redo`);
      }
      
      return {
        rowIndex: currentRow.rowIndex, // Use current row index
        columnId: actionRow.columnId,
        globalId: actionRow.globalId,
        tableId: actionRow.tableId,
        rrn: actionRow.rrn // Use the updated RRN from the action
      };
    });
    
    // Step 1: Make all API calls for row deletion (no state updates)
    for (const rowToDelete of rowsToDelete) {
      await this.callbacks.onRowDeleteWithoutStateUpdate(
        rowToDelete.rowIndex,
        rowToDelete.columnId,
        rowToDelete.globalId,
        rowToDelete.tableId,
        rowToDelete.rrn
      );
    }
    
    // Step 2: Single batched state update
    await this.callbacks.onBulkStateUpdateForRedo({
      rowDeletions: rowsToDelete,
      totalCountChange: -action.deletedRows.length // Negative because we're deleting
    });
  }

  /**
   * Undo a bulk fill operation (restore original values)
   */
  private async undoBulkFill(action: BulkFillAction): Promise<void> {
    // Prepare bulk updates to restore original values
    const bulkUpdates = action.fillOperations.map(operation => ({
      rowIndex: operation.rowIndex,
      columnId: operation.columnId,
      value: operation.originalValue, // Restore original value
      payload: { ...operation.payload, value: operation.originalValue },
      columnIndex: operation.columnIndex
    }));

    // Use bulk update to restore all original values at once
    if (this.callbacks.onBulkCellUpdate) {
      await this.callbacks.onBulkCellUpdate(bulkUpdates);
    }
  }

  /**
   * Redo a bulk fill operation (apply fill values again)
   */
  private async redoBulkFill(action: BulkFillAction): Promise<void> {
    // Prepare bulk updates to apply fill values again
    const bulkUpdates = action.fillOperations.map(operation => ({
      rowIndex: operation.rowIndex,
      columnId: operation.columnId,
      value: operation.newValue, // Apply fill value
      payload: operation.payload,
      columnIndex: operation.columnIndex
    }));

    // Use bulk update to apply all fill values at once
    if (this.callbacks.onBulkCellUpdate) {
      await this.callbacks.onBulkCellUpdate(bulkUpdates);
    }
  }

  /**
   * Pop the most recent undo action from the stack
   * Used when a subsequent action should replace the previous one
   */
  popPreviousUndoAction(): UndoRedoAction | null {
    if (this.state.undoStack.length === 0) {
      return null;
    }
    
    const poppedAction = this.state.undoStack.pop()!;
    return poppedAction;
  }

  /**
   * Add action to undo stack with size management
   */
  private addToUndoStack(action: UndoRedoAction): void {
    this.state.undoStack.push(action);

    // Maintain max stack size
    if (this.state.undoStack.length > this.state.maxStackSize) {
      this.state.undoStack.shift();
    }
  }

  /**
   * Clear the redo stack
   */
  private clearRedoStack(): void {
    this.state.redoStack = [];
  }

  /**
   * Generate unique action ID
   */
  private generateActionId(): string {
    return `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
